import {
  getStudentHonorsPortalState,
  isCurrentHonorsProgramMember
} from '../../../utils/honorsSignups';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const readHeaders = {
  apikey: SUPABASE_KEY
};

const jsonHeaders = {
  apikey: SUPABASE_KEY,
  'Content-Type': 'application/json'
};

async function parseResponse(response, label) {
  let data;

  try {
    data = await response.json();
  } catch (err) {
    data = null;
  }

  if (!response.ok) {
    console.error(`${label} failed:`, response.status, data);

    throw new Error(
      data?.message ||
      data?.error ||
      `${label} failed with HTTP ${response.status}`
    );
  }

  return data;
}

async function findOrCreateHonorsChat() {
  let response = await fetch(
    `${SUPABASE_URL}/rest/v1/chats?chat_type=eq.honors&select=id&limit=1`,
    {
      headers: readHeaders
    }
  );

  let data = await parseResponse(
    response,
    'Fetching Honors chat'
  );

  if (data.length === 0) {
    response = await fetch(
      `${SUPABASE_URL}/rest/v1/chats`,
      {
        method: 'POST',
        headers: {
          ...jsonHeaders,
          Prefer: 'return=representation'
        },
        body: JSON.stringify({
          chat_type: 'honors'
        })
      }
    );

    data = await parseResponse(
      response,
      'Creating Honors chat'
    );
  }

  return data?.[0]?.id || null;
}

async function findOrCreateCourseChat(courseId) {
  let response = await fetch(
    SUPABASE_URL +
      '/rest/v1/chats?chat_type=eq.course&course_id=eq.' +
      courseId +
      '&select=id&limit=1',
    {
      headers: readHeaders
    }
  );

  let data = await parseResponse(
    response,
    'Fetching course chat'
  );

  if (data.length === 0) {
    response = await fetch(
      SUPABASE_URL +
        '/rest/v1/chats',
      {
        method: 'POST',
        headers: {
          ...jsonHeaders,
          Prefer:
            'return=representation'
        },
        body: JSON.stringify({
          chat_type:
            'course',

          course_id:
            courseId
        })
      }
    );

    data = await parseResponse(
      response,
      'Creating course chat'
    );
  }

  return (
    data?.[0]?.id ||
    null
  );
}


export async function fetchInitialStudentChat(
  session,
  courseId = null
) {
  const courseFilter =
    courseId
      ? '&course_id=eq.' +
        courseId
      : '';

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/enrolments?student_id=eq.' +
        session.studentId +
        '&active=eq.true' +
        courseFilter +
        '&select=id,course_id,courses(id,name,is_live,chat_enabled,course_titles(name))',
      {
        headers:
          readHeaders
      }
    );

  const rows =
    await parseResponse(
      response,
      'Fetching initial chat course'
    );

  const enrolment =
    (
      Array.isArray(rows)
        ? rows
        : []
    ).find(
      row =>
        row
          ?.courses
          ?.is_live ===
          true
        &&
        row
          ?.courses
          ?.chat_enabled !==
          false
    );

  if (!enrolment) {
    return null;
  }

  const chatId =
    await findOrCreateCourseChat(
      enrolment.course_id
    );

  if (!chatId) {
    return null;
  }

  return {
    id:
      chatId,

    type:
      'course',

    label:
      enrolment
        .courses
        ?.course_titles
        ?.name
      ||
      enrolment
        .courses
        ?.name
      ||
      'Course Chat',

    courseId:
      enrolment.course_id,

    enrolmentId:
      enrolment.id
  };
}


export async function fetchStudentChats(session) {
  const chats = [];

  // ------------------------------------------------------------
  // Course chats — one per enrolled live course
  // ------------------------------------------------------------

  const enrolResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${session.studentId}&active=eq.true&select=id,course_id,group_number,courses(id,name,course_titles(name),is_live,chat_enabled)`,
    {
      headers: readHeaders
    }
  );

  const enrolments = await parseResponse(
    enrolResponse,
    'Fetching enrolments'
  );

  for (const enrolment of enrolments) {
    if (!enrolment.courses?.is_live) continue;
    if (enrolment.courses?.chat_enabled === false) continue;

    const courseName =
      enrolment.courses?.course_titles?.name ||
      enrolment.courses?.name ||
      'Course';

    // ----------------------------------------------------------
    // Find or create course chat
    // ----------------------------------------------------------

    let chatResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/chats?chat_type=eq.course&course_id=eq.${enrolment.course_id}&select=id&limit=1`,
      {
        headers: readHeaders
      }
    );

    let chatData = await parseResponse(
      chatResponse,
      'Fetching course chat'
    );

    if (chatData.length === 0) {
      const createResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/chats`,
        {
          method: 'POST',
          headers: {
            ...jsonHeaders,
            Prefer: 'return=representation'
          },
          body: JSON.stringify({
            chat_type: 'course',
            course_id: enrolment.course_id
          })
        }
      );

      chatData = await parseResponse(
        createResponse,
        'Creating course chat'
      );
    }

    if (!chatData?.[0]?.id) {
      console.error(
        'Course chat was not returned correctly:',
        enrolment,
        chatData
      );
      continue;
    }

    chats.push({
      id: chatData[0].id,
      type: 'course',
      label: courseName,
      courseId: enrolment.course_id,
      enrolmentId: enrolment.id
    });

    // ----------------------------------------------------------
    // Group chats
    //
    // Chat scope follows actual membership, not assignment
    // instances.
    //
    // - Repeated instances of the same assignment share one chat
    //   whenever their membership is identical.
    // - Scheduled assignments in the same Sequence share one chat
    //   whenever their membership is identical.
    // - If membership differs, that membership scope gets its own
    //   chat.
    //
    // The chats table can keep its existing
    // course_assignment_id + group_number key. We simply choose one
    // deterministic assignment/group row as the canonical storage
    // key for each membership scope.
    // ----------------------------------------------------------

    const allGroupResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignment_group_memberships?select=course_assignment_id,enrolment_id,group_number,course_assignments!inner(id,course_id,assignment_type_id,groups_enabled,group_chat_enabled,assignment_types(name))&course_assignments.course_id=eq.${enrolment.course_id}`,
      {
        headers: readHeaders
      }
    );

    const allGroups = await parseResponse(
      allGroupResponse,
      'Fetching assignment groups'
    );

    const typeSettingsResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${enrolment.course_id}&select=assignment_type_id,sequence_id,schedule_enabled`,
      {
        headers: readHeaders
      }
    );

    const typeSettingsRows = await parseResponse(
      typeSettingsResponse,
      'Fetching assignment sequence settings'
    );

    const sequencesResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_sequences?course_id=eq.${enrolment.course_id}&select=id,name`,
      {
        headers: readHeaders
      }
    );

    const sequenceRows = await parseResponse(
      sequencesResponse,
      'Fetching assignment sequences'
    );

    const settingsByType = new Map(
      (Array.isArray(typeSettingsRows) ? typeSettingsRows : [])
        .map(row => [
          String(row.assignment_type_id),
          row
        ])
    );

    const sequenceNameById = new Map(
      (Array.isArray(sequenceRows) ? sequenceRows : [])
        .map(row => [
          String(row.id),
          String(row.name || '').trim()
        ])
    );

    const usableGroups = (
      Array.isArray(allGroups)
        ? allGroups
        : []
    ).filter(row =>
      row.course_assignments?.groups_enabled &&
      row.course_assignments?.group_chat_enabled !== false
    );

    function membershipSignature(row) {
      return usableGroups
        .filter(candidate =>
          String(candidate.course_assignment_id) ===
            String(row.course_assignment_id) &&
          Number(candidate.group_number) ===
            Number(row.group_number)
        )
        .map(candidate => String(candidate.enrolment_id))
        .sort()
        .join('|');
    }

    const myGroups = usableGroups
      .filter(row =>
        String(row.enrolment_id) ===
        String(enrolment.id)
      )
      .map(row => {
        const assignmentTypeId =
          row.course_assignments?.assignment_type_id;

        const setting = settingsByType.get(
          String(assignmentTypeId)
        );

        const scheduledSequenceId =
          setting?.schedule_enabled === true &&
          setting?.sequence_id
            ? String(setting.sequence_id)
            : null;

        return {
          row,
          assignmentTypeId:
            assignmentTypeId
              ? String(assignmentTypeId)
              : '',
          signature: membershipSignature(row),
          scheduledSequenceId
        };
      });

    const sequenceClusters = new Map();

    myGroups.forEach(item => {
      if (!item.scheduledSequenceId) return;

      const key =
        `${item.scheduledSequenceId}::${item.signature}`;

      if (!sequenceClusters.has(key)) {
        sequenceClusters.set(key, []);
      }

      sequenceClusters.get(key).push(item);
    });

    const typeClusters = new Map();

    myGroups.forEach(item => {
      const key =
        `${item.assignmentTypeId}::${item.signature}`;

      if (!typeClusters.has(key)) {
        typeClusters.set(key, []);
      }

      typeClusters.get(key).push(item);
    });

    const seenGroupScopes = new Set();

    for (const item of myGroups) {
      const sequenceClusterKey =
        item.scheduledSequenceId
          ? `${item.scheduledSequenceId}::${item.signature}`
          : null;

      const sequenceCluster =
        sequenceClusterKey
          ? sequenceClusters.get(sequenceClusterKey) || []
          : [];

      const isSequenceScope =
        !!item.scheduledSequenceId &&
        sequenceCluster.length > 1;

      const typeClusterKey =
        `${item.assignmentTypeId}::${item.signature}`;

      const scopeItems =
        isSequenceScope
          ? sequenceCluster
          : typeClusters.get(typeClusterKey) || [item];

      const scopeKey =
        isSequenceScope
          ? `sequence::${sequenceClusterKey}`
          : `assignment::${typeClusterKey}`;

      if (seenGroupScopes.has(scopeKey)) {
        continue;
      }

      seenGroupScopes.add(scopeKey);

      const canonical = [...scopeItems]
        .sort((a, b) => {
          const assignmentCompare =
            String(a.row.course_assignment_id)
              .localeCompare(
                String(b.row.course_assignment_id)
              );

          if (assignmentCompare !== 0) {
            return assignmentCompare;
          }

          return (
            Number(a.row.group_number) -
            Number(b.row.group_number)
          );
        })[0];

      const canonicalAssignmentId =
        canonical.row.course_assignment_id;

      const canonicalGroupNumber =
        canonical.row.group_number;

      let groupChatResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/chats?chat_type=eq.group&course_assignment_id=eq.${canonicalAssignmentId}&group_number=eq.${canonicalGroupNumber}&select=id&limit=1`,
        {
          headers: readHeaders
        }
      );

      let groupChatData = await parseResponse(
        groupChatResponse,
        'Fetching group chat'
      );

      if (groupChatData.length === 0) {
        const createResponse = await fetch(
          `${SUPABASE_URL}/rest/v1/chats`,
          {
            method: 'POST',
            headers: {
              ...jsonHeaders,
              Prefer:
                'resolution=merge-duplicates,return=representation'
            },
            body: JSON.stringify({
              chat_type: 'group',
              course_assignment_id:
                canonicalAssignmentId,
              group_number:
                canonicalGroupNumber
            })
          }
        );

        groupChatData = await parseResponse(
          createResponse,
          'Creating group chat'
        );
      }

      if (!groupChatData?.[0]?.id) {
        console.error(
          'Group chat was not returned correctly:',
          canonical.row,
          groupChatData
        );
        continue;
      }

      const labelResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/assignment_group_labels?course_assignment_id=eq.${canonicalAssignmentId}&group_number=eq.${canonicalGroupNumber}&select=name&limit=1`,
        {
          headers: readHeaders
        }
      );

      const labelRows = await parseResponse(
        labelResponse,
        'Fetching Assignment Group name'
      );

      const groupName =
        String(labelRows?.[0]?.name || '').trim() ||
        `Group ${canonicalGroupNumber}`;

      let chatLabel;

      if (isSequenceScope) {
        const sequenceName =
          sequenceNameById.get(
            item.scheduledSequenceId
          ) ||
          'Sequence';

        chatLabel =
          `${sequenceName} - ${groupName}`;
      } else {
        const assignmentName =
          canonical.row.course_assignments
            ?.assignment_types?.name ||
          'Assignment';

        chatLabel =
          `${courseName} · ${assignmentName} · ${groupName}`;
      }

      chats.push({
        id: groupChatData[0].id,
        type: 'group',
        label: chatLabel,
        courseId: enrolment.course_id,
        enrolmentId: enrolment.id
      });
    }

  }

 // ------------------------------------------------------------
// Honors chat
//
// Program membership comes from the Honors CMS.
// HSS is not authoritative for Honors-program status.
// ------------------------------------------------------------

  let isHonors = false;

  try {
    const portalState =
      await getStudentHonorsPortalState(
        session
      );

    isHonors =
      isCurrentHonorsProgramMember(
        portalState
      );

  } catch (err) {
    console.error(
      'Could not determine CMS Honors status:',
      err
    );
  }

  if (isHonors) {
    const honorsChatId =
      await findOrCreateHonorsChat();

    if (honorsChatId) {
      chats.push({
        id: honorsChatId,
        type: 'honors',
        label: 'Honors Chat'
      });
    }
  }

  return chats;
}